import vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import vtkDataArray from '@kitware/vtk.js/Common/Core/DataArray';

// GPU yếu (iGPU) không cấp nổi texture 3D lớn -> glTexStorage3D lỗi, khối đen. Hạ độ phân giải
// khối cho vừa MAX_3D_TEXTURE_SIZE để render 3D được (chấp nhận giảm nét). CHỈ dùng cho view 3D.
let max3D: number | null = null;
function getMax3DTextureSize(): number {
  if (max3D != null) return max3D;
  try {
    const gl = document.createElement('canvas').getContext('webgl2');
    max3D = (gl && gl.getParameter(gl.MAX_3D_TEXTURE_SIZE)) || 2048;
  } catch {
    max3D = 2048;
  }
  return max3D!; // luôn được gán ở try/catch trên; TS không thu hẹp biến module-level
}

export function fitVolumeToGpu(img: vtkImageData): vtkImageData {
  const maxTex = getMax3DTextureSize();
  const [dx, dy, dz] = img.getDimensions();
  if (Math.max(dx, dy, dz) <= maxTex) return img; // vừa GPU -> giữ nguyên full-res

  const cap = Math.min(maxTex, 512); // 3D overview không cần full-res; giữ output nhỏ cho nhanh
  const sx = Math.ceil(dx / cap);
  const sy = Math.ceil(dy / cap);
  const sz = Math.ceil(dz / cap);
  const nx = Math.ceil(dx / sx);
  const ny = Math.ceil(dy / sy);
  const nz = Math.ceil(dz / sz);

  const scalars = img.getPointData().getScalars();
  const nc = scalars.getNumberOfComponents();
  const src = scalars.getData() as
    | Int16Array
    | Uint16Array
    | Uint8Array
    | Float32Array;
  const Ctor = src.constructor as { new (len: number): typeof src };
  const out = new Ctor(nx * ny * nz * nc);

  let o = 0;
  for (let z = 0; z < nz; z += 1) {
    const zz = Math.min(z * sz, dz - 1);
    for (let y = 0; y < ny; y += 1) {
      const rowBase = (zz * dy + Math.min(y * sy, dy - 1)) * dx;
      for (let x = 0; x < nx; x += 1) {
        const si = (rowBase + Math.min(x * sx, dx - 1)) * nc;
        for (let c = 0; c < nc; c += 1) {
          out[o] = src[si + c];
          o += 1;
        }
      }
    }
  }

  const [spx, spy, spz] = img.getSpacing();
  const result = vtkImageData.newInstance({
    origin: img.getOrigin(),
    spacing: [spx * sx, spy * sy, spz * sz],
    extent: [0, nx - 1, 0, ny - 1, 0, nz - 1],
  });
  result.setDirection(img.getDirection()); // direction không nằm trong initial values -> set sau khi tạo
  result.getPointData().setScalars(
    vtkDataArray.newInstance({
      name: scalars.getName(),
      numberOfComponents: nc,
      values: out,
    })
  );
  return result;
}
